import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../firebase';
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPhoneNumber,
  RecaptchaVerifier,
} from 'firebase/auth';

// Feature flag for phone auth - enable in production
const PHONE_AUTH_ENABLED = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENABLE_PHONE_AUTH === 'true';

const FirebaseAuthContext = createContext();

export function useFirebaseAuth() {
  return useContext(FirebaseAuthContext);
}

export function FirebaseAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [confirmationResult, setConfirmationResult] = useState(null);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Google login
  const login = async () => {
    if (loading) return; // Prevent multiple popups
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } finally {
      setLoading(false);
    }
  };

  // Email/password login
  const loginWithEmail = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  // Email/password registration
  const registerWithEmail = (email, password) =>
    createUserWithEmailAndPassword(auth, email, password);

  // Phone login (step 1: send code)
  const sendPhoneCode = PHONE_AUTH_ENABLED ? (phoneNumber, appVerifier) =>
    signInWithPhoneNumber(auth, phoneNumber, appVerifier)
      .then((result) => {
        setConfirmationResult(result);
        return result;
      }) : () => Promise.reject(new Error('Phone authentication is disabled'));

  // Phone login (step 2: verify code)
  const verifyPhoneCode = PHONE_AUTH_ENABLED ? (code) =>
    confirmationResult.confirm(code) : () => Promise.reject(new Error('Phone authentication is disabled'));

  // Recaptcha setup for phone login
  const setupRecaptcha = PHONE_AUTH_ENABLED ? (containerId) => {
    if (!recaptchaVerifier) {
      try {
        const verifier = new RecaptchaVerifier(auth, containerId, {
          size: 'invisible',
          callback: () => {},
          'expired-callback': () => {}
        });
        setRecaptchaVerifier(verifier);
        return verifier;
      } catch (error) {
        console.error('Error setting up recaptcha:', error);
        throw error;
      }
    }
    return recaptchaVerifier;
  } : () => Promise.reject(new Error('Phone authentication is disabled'));

  const logout = async () => {
    // Clear recaptcha verifier if it exists
    if (recaptchaVerifier) {
      recaptchaVerifier.clear();
      setRecaptchaVerifier(null);
    }
    setConfirmationResult(null);
    await signOut(auth);
  };

  // Clear recaptcha verifier function
  const clearRecaptcha = PHONE_AUTH_ENABLED ? () => {
    if (recaptchaVerifier) {
      recaptchaVerifier.clear();
      setRecaptchaVerifier(null);
    }
  } : () => {};

  return (
    <FirebaseAuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        loginWithEmail,
        registerWithEmail,
        ...(PHONE_AUTH_ENABLED && {
          sendPhoneCode,
          verifyPhoneCode,
          setupRecaptcha,
          clearRecaptcha,
          confirmationResult,
        }),
        phoneAuthEnabled: PHONE_AUTH_ENABLED,
      }}
    >
      {children}
    </FirebaseAuthContext.Provider>
  );
} 